import { useEffect, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { WERSJA_APLIKACJI } from '@/lib/diagnostyka'
import { sledzWersje, twardeOdswiezenie } from '@/lib/aktualizacja'
import { nasluchujAktualizacji } from '@/lib/serviceWorker'
import type { Aktualizacja } from '@/lib/serviceWorker'

/**
 * Po jakim czasie przypominamy się komuś, kto odłożył odświeżenie.
 *
 * Dziesięć minut wystarcza, żeby dokończyć wypełniany formularz, a jednocześnie
 * nie pozwala pracować przez cały dzień na wersji, której już nie ma.
 */
const PRZYPOMNIENIE_MS = 10 * 60 * 1000

/**
 * Okno informujące o nowej wersji aplikacji.
 *
 * Odświeżenia nie robimy automatycznie. Przeładowanie strony w trakcie
 * wypełniania karty ucznia skasowałoby to, co ktoś właśnie wpisał — a nowa
 * wersja rzadko jest tak pilna jak niezapisana praca. Zamiast tego pytamy
 * i wracamy z pytaniem, jeśli ktoś odłożył je na później.
 *
 * Sygnał przychodzi z dwóch źródeł (patrz `lib/aktualizacja.ts`): od service
 * workera tam, gdzie w ogóle działa, i z odpytywania `version.json` wszędzie.
 * Oba prowadzą do tego samego okna, więc użytkownik widzi jedno zachowanie
 * niezależnie od tego, czy aplikacja stoi po HTTPS, czy po zwykłym HTTP.
 */
export function UpdatePrompt() {
  const [widoczne, setWidoczne] = useState(false)
  // Uchwyt od service workera. Bez niego zostaje zwykłe przeładowanie —
  // wystarczające, bo `index.html` serwowane jest z `no-cache`, więc wraca
  // razem z nowymi nazwami plików.
  const aktualizacja = useRef<Aktualizacja | null>(null)

  useEffect(() => {
    const odepnijSW = nasluchujAktualizacji((gotowa) => {
      aktualizacja.current = gotowa
      setWidoczne(true)
    })
    const odepnijWersje = sledzWersje({
      biezaca: WERSJA_APLIKACJI,
      onNowa: () => setWidoczne(true),
    })

    return () => {
      odepnijSW()
      odepnijWersje()
    }
  }, [])

  const odswiez = () => {
    // Ścieżka czysta: service worker ma gotową paczkę i sam się przełączy.
    if (aktualizacja.current) {
      aktualizacja.current.zastosuj()
      return
    }
    // Ścieżka twarda: o nowej wersji wiemy z `version.json`, a service worker
    // jeszcze jej nie zauważył. Samo przeładowanie dostałoby starą powłokę
    // z jego pamięci podręcznej i okno wracałoby w kółko.
    void twardeOdswiezenie()
  }

  const odloz = () => {
    setWidoczne(false)
    window.setTimeout(() => setWidoczne(true), PRZYPOMNIENIE_MS)
  }

  return (
    <Modal
      open={widoczne}
      onClose={odloz}
      title="Dostępna jest nowa wersja"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={odloz}>
            Za chwilę
          </Button>
          <Button icon={<RefreshCw size={16} />} onClick={odswiez}>
            Odśwież teraz
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-on-surface-variant">
        Aplikacja została zaktualizowana. Odśwież, żeby wczytać nową wersję — zajmie to chwilę i nie
        wylogowuje.
      </p>
      <p className="mt-3 text-sm leading-relaxed text-on-surface-variant">
        Jeśli masz otwarty formularz, dokończ go i odśwież za moment. Przypomnimy się.
      </p>
    </Modal>
  )
}
